package top.wcpe.allincore.dsl.compiler.semantic

import top.wcpe.allincore.dsl.compiler.api.ProjectTypeTable
import top.wcpe.allincore.dsl.compiler.ast.ElementAst
import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.ast.ScreenAst
import top.wcpe.allincore.dsl.compiler.ast.StmtAst
import top.wcpe.allincore.dsl.compiler.ast.dataClassDecls
import top.wcpe.allincore.dsl.compiler.diag.DiagnosticCollector
import top.wcpe.allincore.dsl.compiler.type.IrType
import top.wcpe.allincore.dsl.compiler.type.TypeResolver

/**
 * M10 Stage 1：类型约束校验器（聚合器形态）。
 *
 * 本类不再持有诊断逻辑，仅负责维护共享状态容器 [VarTypeEnv]、在 [check] 入口为每个 fun / action 重置作用域，
 * 并递归遍历 ScreenAst 全部 fun / action / 元素属性，按节点类型分发到 5 个子 checker 的 hook 方法。
 *
 * 子 checker 与诊断码归属：
 * - [LocalVarTypeChecker]：E620 / E621 / E660 / E662 / E670（var 与形参 / for 迭代变量相关）
 * - [StateFieldChecker]：E624 / E661（state 字段赋值相关）
 * - [NullSafetyChecker]：E666 / E669（可空类型直接解引用 / data 字段名错误）
 * - [ControlFlowTypeChecker]：E665 / E667 / E668（when / Elvis / is）
 * - [LambdaParamChecker]：E663 / E664（lambda 形参未标注 / net.request callback 标注不容）
 *
 * 嵌套作用域：fun / action 入口走 [VarTypeEnv.resetForNewFun]；嵌套 fun 走 [VarTypeEnv.withScopedDeclaredVars]
 * 保存外层、推入内层、退出恢复；嵌套 for 循环由 [VarTypeEnv.withForLoopVar] 维护迭代变量栈。
 * AssignStmt 的 6 个 hook 调度顺序与拆分前完全一致——顺序变更会改变诊断报告顺序，影响测试断言。
 *
 * @property crossFileTypes M10 Stage 4：项目级跨文件类型表，注入到 [VarTypeEnv.crossFileTypes]
 *   供 [NullSafetyChecker] 在 E669 检查时反查跨文件 data 字段。默认空表，向后兼容。
 */
class TypeConstraintChecker(
    private val diag: DiagnosticCollector,
    private val crossFileTypes: ProjectTypeTable = ProjectTypeTable.EMPTY,
) {

    private val env: VarTypeEnv = VarTypeEnv()
    private val localVar = LocalVarTypeChecker(diag, env)
    private val stateField = StateFieldChecker(diag, env)
    private val nullSafety = NullSafetyChecker(diag, env)
    private val controlFlow = ControlFlowTypeChecker(diag, env)
    private val lambdaParam = LambdaParamChecker(diag, env)

    fun check(screen: ScreenAst) {
        env.reset()
        env.crossFileTypes = crossFileTypes
        // 构建 state 字段类型映射表：原始 TypeAst 供空赋值检查，IrType 供二次赋值类型一致性检查
        screen.state?.entries?.forEach { entry ->
            entry.type?.let { ast ->
                env.stateFieldTypes[entry.name] = ast
                // 解析失败的字段不入 IrType 表，让 E621 在 StateTypeChecker 路径处理
                TypeResolver.resolve(ast)?.let { ir ->
                    env.stateFieldIrTypes[entry.name] = ir
                }
            }
        }
        // 同文件 data 类：登记类名 + 字段名集合，供 E669 校验在 NullSafetyChecker 中反查
        screen.dataClassDecls().forEach { decl ->
            env.dataTypeNames += decl.name
            val fields = env.localDataFields.getOrPut(decl.name) { mutableSetOf() }
            decl.fields.forEach { fields += it.name }
        }
        screen.funs.forEach { fn ->
            env.resetForNewFun()
            env.currentFunParams += fn.params
            walkStmts(fn.body)
        }
        walkElementProps(screen.root)
    }

    /** 元素属性表达式里也可能含 lambda / 后续嵌套 var 声明。 */
    private fun walkElementProps(element: top.wcpe.allincore.dsl.compiler.ast.ElementAst) {
        element.props.forEach { walkPropAssign(it) }
        element.modifiers.forEach { modifier ->
            modifier.fields.forEach { walkPropAssign(it) }
        }
        // RFC-RECOMPOSITION：conditional 的 subject / branch.condition 同样可能含 lambda / 类型约束目标
        element.conditionalContent?.let { content ->
            content.subject?.let(::walkExpr)
            for (branch in content.branches) {
                branch.condition?.let(::walkExpr)
            }
        }
        // $for：iterable 表达式同样可能含 lambda / 类型约束目标
        element.forContent?.let { walkExpr(it.iterable) }
        top.wcpe.allincore.dsl.compiler.ast.ElementWalk.directDescendants(element).forEach(::walkElementProps)
    }

    /**
     * 元素 prop 赋值入口：在 walkExpr 之前先看一眼"prop = lambda"形态，
     * 让 [LambdaParamChecker] 拿到 prop 名上下文报 E663。
     */
    private fun walkPropAssign(prop: ElementAst.PropAssign) {
        val value = prop.value
        if (value is ExprAst.LambdaExpr) {
            lambdaParam.onLambdaInPropAssign(prop.name, value)
        }
        walkExpr(value)
    }

    private fun walkStmts(stmts: List<StmtAst>) {
        stmts.forEach { walkStmt(it) }
    }

    private fun walkStmt(s: StmtAst) {
        when (s) {
            is StmtAst.VarDeclStmt -> {
                // 先对初始化表达式内部嵌套 var / lambda 做递归扫描
                walkExpr(s.initializer)
                if (s.typeAnnotation != null) {
                    localVar.onVarDecl(s.name, s.typeAnnotation, s.initializer)
                } else {
                    localVar.onUntypedVarDecl(s.name, s.initializer)
                }
            }
            is StmtAst.AssignStmt -> {
                walkExpr(s.rhs)
                // 6 个 hook 顺序与拆分前严格一致，调度顺序影响诊断报告顺序
                stateField.onAssignStateNull(s)
                localVar.onAssignDeclaredVar(s)
                localVar.onAssignParam(s)
                localVar.onAssignForLoopVar(s)
                localVar.onAssignUntypedVar(s)
                stateField.onAssignStateValue(s)
            }
            is StmtAst.ExprStmt -> walkExpr(s.call)
            is StmtAst.IfStmt -> {
                walkExpr(s.cond)
                // M10 Stage 6 / 数据流分析：识别 `x is Type` / `x != null`，then 分支内
                // 把 x 局部窄化；else 分支不做反向窄化（M11+ 再扩展）
                val narrowing = analyzeNarrowing(s.cond)
                if (narrowing != null) {
                    env.withNarrowedType(narrowing.name, narrowing.narrowedType) {
                        walkStmts(s.thenBody)
                    }
                } else {
                    walkStmts(s.thenBody)
                }
                walkStmts(s.elseBody)
            }
            is StmtAst.WhileStmt -> { walkExpr(s.cond); walkStmts(s.body) }
            is StmtAst.ForInStmt -> {
                walkExpr(s.iterable)
                // 进入 body 前压栈、退出后弹栈；try / finally 守住嵌套循环退栈不互相污染
                env.withForLoopVar(s.varName) { walkStmts(s.body) }
            }
            is StmtAst.WhenStmt -> walkExpr(s.whenExpr)
            is StmtAst.ReturnStmt -> s.value?.let { walkExpr(it) }
            is StmtAst.TryStmt -> { walkStmts(s.tryBody); walkStmts(s.catchBody); walkStmts(s.finallyBody) }
            is StmtAst.FunDeclStmt -> {
                // 嵌套 fun：进入子作用域前保存当前作用域，结束后恢复——避免内层 var 名污染外层
                env.withScopedDeclaredVars(s.decl.params) { walkStmts(s.decl.body) }
            }
            // batch 语句：递归遍历 body（与 TryStmt 的 tryBody 处理一致）
            is StmtAst.BatchStmt -> walkStmts(s.body)
            is StmtAst.BreakStmt, is StmtAst.ContinueStmt -> Unit
        }
    }

    /** 表达式递归扫描，覆盖 lambda body / if-expr body 等嵌套语句块内的 var 声明。 */
    private fun walkExpr(e: ExprAst) {
        when (e) {
            is ExprAst.LambdaExpr -> walkStmts(e.body)
            is ExprAst.IfExpr -> {
                walkExpr(e.cond)
                walkStmts(e.thenBody)
                walkStmts(e.elseBody)
            }
            is ExprAst.WhenExpr -> {
                walkExpr(e.subject)
                e.branches.forEach { br ->
                    br.values.forEach { walkExpr(it) }
                    walkStmts(br.body)
                }
                e.elseBody?.let { walkStmts(it) }
                // 分支返回类型两两不容时报 E665 INFO
                controlFlow.onWhenExpr(e)
            }
            is ExprAst.CallExpr -> {
                walkExpr(e.callee)
                e.args.forEach { walkExpr(it) }
                e.namedArgs.forEach { walkExpr(it.value) }
            }
            is ExprAst.AwaitExpr -> {
                e.netRequestCall?.let { walkExpr(it) }
                e.suspendCall?.let { walkExpr(it) }
                e.tailExpr?.let { walkExpr(it) }
            }
            is ExprAst.BinaryOp -> { walkExpr(e.left); walkExpr(e.right) }
            is ExprAst.UnaryOp -> walkExpr(e.operand)
            is ExprAst.MemberAccess -> {
                walkExpr(e.target)
                nullSafety.onMemberAccess(e)
            }
            is ExprAst.SafeMemberAccess -> walkExpr(e.target)
            // M10 Stage 6：`target!!` 透传 target；类型窄化由 TypeInferencer.inferExpr 完成
            is ExprAst.NonNullAssert -> walkExpr(e.target)
            is ExprAst.MemberCall -> {
                walkExpr(e.target)
                e.args.forEach { walkExpr(it) }
                e.namedArgs.forEach { walkExpr(it.value) }
            }
            is ExprAst.SafeMemberCall -> {
                walkExpr(e.target)
                e.args.forEach { walkExpr(it) }
            }
            is ExprAst.Elvis -> {
                walkExpr(e.left)
                walkExpr(e.right)
                // 左侧 inner 与右侧不容时报 E667 INFO
                controlFlow.onElvis(e)
            }
            is ExprAst.Ternary -> {
                walkExpr(e.cond); walkExpr(e.thenExpr); walkExpr(e.elseExpr)
            }
            is ExprAst.Interp -> e.parts.forEach {
                if (it is ExprAst.Interp.InterpPart.ExprPart) walkExpr(it.expr)
            }
            is ExprAst.ListLiteralExpr -> e.elements.forEach { walkExpr(it) }
            is ExprAst.MapLiteralExpr -> e.entries.forEach { walkExpr(it.value) }
            is ExprAst.RangeExpr -> { walkExpr(e.start); walkExpr(e.endValue) }
            is ExprAst.IndexExpr -> { walkExpr(e.target); walkExpr(e.index) }
            is ExprAst.BuiltinCallExpr -> e.args.forEach { walkExpr(it) }
            is ExprAst.NetSubscribeCall -> walkExpr(e.handler)
            is ExprAst.NetRequestCall -> {
                e.payload.forEach { walkExpr(it.value) }
                // E664：在 walk 之前先看一眼 callback 形态，让 LambdaParamChecker 拿到 lambda 本体
                e.callback?.let { lambdaParam.onNetRequestCallback(it) }
                e.callback?.let { walkExpr(it) }
                e.timeoutMs?.let { walkExpr(it) }
            }
            is ExprAst.IsExpr -> {
                walkExpr(e.target)
                // is 运算符目标类型未识别时报 E668 INFO
                controlFlow.onIsExpr(e)
            }
            is ExprAst.Literal, is ExprAst.NullLit, is ExprAst.IdentRef,
            is ExprAst.LocalRef, is ExprAst.StateRef, is ExprAst.SessionRef, is ExprAst.PersistRef, is ExprAst.DerivedRef, is ExprAst.PropsRef,
            is ExprAst.ThemeRef, is ExprAst.FunRefExpr -> Unit
        }
    }

    // ==================== M10 Stage 6 / 数据流窄化辅助 ====================

    /**
     * IfStmt.cond 上能识别出的窄化目标。
     *
     * - [name]：被窄化的 LocalRef 名
     * - [narrowedType]：进入 then 分支后该名字应当被覆盖为的 IrType
     */
    private data class NarrowingHint(val name: String, val narrowedType: IrType)

    /**
     * 识别 IfStmt.cond 是否形如 `LocalRef is TypeName` 或 `LocalRef != null`，
     * 返回窄化目标。其它形态返回 null（不参与窄化）。
     *
     * 第一版仅支持最基础的两种形态：
     * - `if (x is TypeName)`：x 在 then 分支内被视为 TypeName
     * - `if (x != null)`：x 在 then 分支内被视为 unwrap 后的非空内层类型
     *
     * 复合条件（`a && b`、`a || b`）、嵌套属性（`obj.field is X`）、跨文件 data 类
     * 等均留给后续 milestone。
     */
    private fun analyzeNarrowing(cond: ExprAst): NarrowingHint? {
        // 形态一：x is TypeName
        if (cond is ExprAst.IsExpr) {
            val target = cond.target
            if (target is ExprAst.LocalRef) {
                val resolved = resolveTypeName(cond.typeName) ?: return null
                return NarrowingHint(target.name, resolved)
            }
            return null
        }
        // 形态二：x != null（左右两侧皆可为 LocalRef）
        if (cond is ExprAst.BinaryOp && cond.op == "!=") {
            val ordered = orderRefAndNull(cond.left, cond.right) ?: return null
            val ref = ordered.first
            val original = env.currentVarEnv()[ref.name] ?: return null
            // 仅可空类型才需要窄化；非可空类型在 then 分支保持原样即可
            if (original !is IrType.Nullable) return null
            return NarrowingHint(ref.name, original.inner)
        }
        return null
    }

    /**
     * 把 `is` 运算符使用的 [typeName] 解析为 [IrType]。
     *
     * 与 [ControlFlowTypeChecker.onIsExpr] 中的"类型名识别"在功能上有重叠（后者只判断"能否
     * 解析"），但目标不同：本方法需要拿到具体 IrType 才能做窄化，无法直接复用对方的 boolean
     * 结果。第一版独立实现一份；如后续需求扩展（如复用同一份"内置类型表"），再考虑抽取共用 helper。
     *
     * 解析规则（与 ExprEvaluator.checkIsType 与 ControlFlowTypeChecker.IS_TARGET_BUILTIN_CONTAINERS 对齐）：
     * - 内置基础：Number / Int / Long / Double / Float → NumberT；String → StringT；Bool / Boolean → BooleanT；
     *   Map → 宽 MapT；List → 宽 ListT；Any → AnyT；Unit → UnitT
     * - 同文件 data 类：包装为 [IrType.DataInstance]
     * - 末尾 `?`：解析后用 [IrType.Nullable] 包装
     * - 其它（含跨文件 data 类）：返回 null，不参与窄化（保留 INFO 级 E666 行为）
     */
    private fun resolveTypeName(typeName: String): IrType? {
        val baseName = typeName.removeSuffix("?").trim()
        if (baseName.isEmpty()) return null
        val base: IrType = when (baseName) {
            "Number", "Int", "Long", "Double", "Float" -> IrType.NumberT
            "String" -> IrType.StringT
            "Bool", "Boolean" -> IrType.BooleanT
            "Map" -> IrType.MapT()
            "List" -> IrType.ListT()
            "Any" -> IrType.AnyT
            "Unit" -> IrType.UnitT
            else -> {
                if (baseName in env.dataTypeNames) IrType.DataInstance(baseName) else return null
            }
        }
        return if (typeName.endsWith("?")) IrType.Nullable(base) else base
    }

    /**
     * 识别 BinaryOp 中"一侧 LocalRef、另一侧 NullLit"形态。
     *
     * 返回 (LocalRef, NullLit) 二元组；不匹配该形态时返回 null。
     */
    private fun orderRefAndNull(a: ExprAst, b: ExprAst): Pair<ExprAst.LocalRef, ExprAst.NullLit>? {
        if (a is ExprAst.LocalRef && b is ExprAst.NullLit) return a to b
        if (b is ExprAst.LocalRef && a is ExprAst.NullLit) return b to a
        return null
    }
}
